import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

const CONFIG = {
  fr: {
    don: { title: "Faire un don", tag: "§ Un souffle qui compte", presets: [10, 30, 50, 100], min: 1, micro: "Reçu fiscal envoyé par email : 66 % de déduction (particuliers), 60 % (entreprises)." },
    adhesion: { title: "J'adhère aux Arteurs", tag: "§ La porte des Arteurs", presets: [30, 50, 100], min: 30, micro: "Cotisation annuelle à partir de 30 €. Carte de membre nominative envoyée sous 15 jours." },
  },
  en: {
    don: { title: "Make a donation", tag: "§ A breath that counts", presets: [10, 30, 50, 100], min: 1, micro: "Tax receipt sent by email: 66% deduction (individuals), 60% (companies) under French law." },
    adhesion: { title: "Join Les Arteurs", tag: "§ The Arteurs' door", presets: [30, 50, 100], min: 30, micro: "Annual membership from €30. Personalised membership card sent within 15 days." },
  },
};

export default function PaymentModal({ open, kind = "don", onClose }) {
  const { lang } = useLang();
  const en = lang === "en";
  const conf = CONFIG[lang][kind] || CONFIG[lang].don;
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wallConsent, setWallConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const effective = custom !== "" ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!effective || isNaN(effective) || effective < conf.min) {
      toast.error(en ? `Minimum amount: ${conf.min} €.` : `Montant minimum : ${conf.min} €.`);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind, amount: effective, name, email,
        wall_consent: kind === "don" ? wallConsent : false,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          data-testid="payment-modal"
        >
          <div className="absolute inset-0 bg-fg/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
            className="relative bg-bg border border-fg/15 w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 md:p-10"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-5 right-5 p-2 text-fg/60 hover:text-cinabre transition-colors"
              data-testid="payment-modal-close"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>

            <div className="meta-label text-cinabre">{conf.tag}</div>
            <h2 className="mt-3 font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight">
              {conf.title}
            </h2>
            <p className="mt-4 text-fg/70 text-sm leading-relaxed">{conf.micro}</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <div className="meta-label text-fg/55 mb-3">§ {en ? "Amount" : "Montant"}</div>
                <div className="grid grid-cols-4 gap-2">
                  {conf.presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => { setAmount(p); setCustom(""); }}
                      className={`py-3 border font-mono text-sm transition-colors ${amount === p && custom === "" ? "border-cinabre bg-cinabre text-bg" : "border-fg/20 hover:border-cinabre"}`}
                      data-testid={`payment-preset-${p}`}
                    >
                      {p} €
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={conf.min}
                  step="1"
                  value={custom}
                  onChange={(e) => { setCustom(e.target.value); setAmount(null); }}
                  placeholder={en ? `Custom amount (min. ${conf.min} €)` : `Montant libre (min. ${conf.min} €)`}
                  className="mt-3 w-full bg-bg border border-fg/20 px-4 py-3 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                  data-testid="payment-custom-amount"
                />
              </div>

              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={en ? "Your name *" : "Votre nom *"}
                className="w-full bg-bg border border-fg/20 px-4 py-3 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                data-testid="payment-name"
              />
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={en ? "Your email * (confirmation & receipt)" : "Votre email * (confirmation & reçu)"}
                className="w-full bg-bg border border-fg/20 px-4 py-3 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                data-testid="payment-email"
              />

              {kind === "don" && (
                <label className="flex items-start gap-3 cursor-pointer text-sm text-fg/75 leading-relaxed">
                  <input
                    type="checkbox"
                    checked={wallConsent}
                    onChange={(e) => setWallConsent(e.target.checked)}
                    className="mt-1 accent-[#e63946]"
                    data-testid="payment-wall-consent"
                  />
                  <span>{en ? <>I agree that my <strong>first name</strong> appears on the donors' wall — discreet, sober, eternal.</> : <>J'accepte que mon <strong>prénom</strong> apparaisse sur le mur des donateurs — discret, sobre, éternel.</>}</span>
                </label>
              )}

              <button
                type="submit"
                disabled={sending || !effective}
                className="w-full inline-flex items-center justify-center gap-3 bg-cinabre text-bg px-8 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors disabled:opacity-50"
                data-testid="payment-submit"
              >
                {sending ? (en ? "Redirecting…" : "Redirection…") : <>{en ? "Pay" : "Payer"} {effective ? `${effective} €` : ""} <ArrowRight size={14} /></>}
              </button>
              <div className="flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">
                <Lock size={11} /> {en ? "Secure payment by Stripe" : "Paiement sécurisé par Stripe"}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
